import { Button, IconButton, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { tokens } from "../../../theme/tokens";

const focusRing = {
  "&:focus-visible": {
    outline: `${tokens.focus.width} solid ${tokens.color.focus.ring}`,
    outlineOffset: tokens.focus.offset,
  },
} as const;

const motion = {
  transition: `background-color ${tokens.motion.instant} ${tokens.motion.easing}, border-color ${tokens.motion.instant} ${tokens.motion.easing}, color ${tokens.motion.instant} ${tokens.motion.easing}`,
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
} as const;

export const PrimaryButton = styled(Button)({
  backgroundColor: tokens.color.brand.primaryAction,
  color: tokens.color.brand.onPrimary,
  borderRadius: `${tokens.radius.sm} !important`,
  padding: `${tokens.space[2]} ${tokens.space[3]} !important`,
  fontWeight: tokens.type.denseStrong.weight,
  textTransform: "capitalize",
  boxShadow: "none",
  ...motion,
  ...focusRing,
  "&:hover": {
    backgroundColor: tokens.color.brand.primaryActionHover,
    boxShadow: "none",
  },
  "&:active": {
    backgroundColor: tokens.color.brand.primaryActionPressed,
  },
  "&:disabled": {
    color: tokens.color.text.disabled,
  },
});

export const StyledTabButton = styled(Button)({
  backgroundColor: tokens.color.surface.default,
  color: tokens.color.text.secondary,
  border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
  borderRadius: `${tokens.radius.sm} !important`,
  padding: `${tokens.space[2]} ${tokens.space[3]} !important`,
  textTransform: "capitalize",
  boxShadow: "none",
  ...motion,
  ...focusRing,
  "&:hover": {
    backgroundColor: tokens.color.surface.hover,
    color: tokens.color.text.primary,
  },
  "&.active": {
    backgroundColor: tokens.color.brand.primary,
    color: tokens.color.brand.onPrimary,
    borderColor: tokens.color.brand.primary,
    fontWeight: tokens.type.denseStrong.weight,
  },
  "&:disabled": {
    color: tokens.color.text.disabled,
  },
});

export const DeleteButton = styled(Button)({
  backgroundColor: tokens.color.surface.default,
  color: tokens.color.status.error,
  border: `${tokens.borderWidth} solid ${tokens.color.status.error}`,
  borderRadius: `${tokens.radius.sm} !important`,
  padding: `${tokens.space[2]} ${tokens.space[2]} !important`,
  textTransform: "capitalize",
  boxShadow: "none",
  minWidth: "auto",
  marginLeft: tokens.space[2],
  ...motion,
  ...focusRing,
  "& .MuiButton-startIcon": {
    marginRight: tokens.space[1],
  },
  "&:hover": {
    backgroundColor: tokens.color.status.error,
    color: tokens.color.brand.onPrimary,
    borderColor: tokens.color.status.error,
  },
  "&:disabled": {
    color: tokens.color.text.disabled,
    borderColor: tokens.color.border.default,
  },
});

export const StyledIconButton = styled(IconButton)({
  backgroundColor: tokens.color.surface.default,
  border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
  borderRadius: `${tokens.radius.sm} !important`,
  boxShadow: "none",
  padding: tokens.space[1],
  color: tokens.color.text.secondary,
  ...motion,
  ...focusRing,
  "&:hover": {
    backgroundColor: tokens.color.surface.hover,
    color: tokens.color.text.primary,
  },
  "&.active": {
    backgroundColor: tokens.color.brand.primary,
    borderColor: tokens.color.brand.primary,
    color: tokens.color.brand.onPrimary,
  },
  "&:disabled": {
    color: tokens.color.text.disabled,
  },
});

export const BackButton = styled(IconButton)({
  minWidth: "40px",
  width: "40px",
  height: "40px",
  borderRadius: tokens.radius.sm,
  padding: 0,
  backgroundColor: tokens.color.surface.default,
  color: tokens.color.text.primary,
  border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
  boxShadow: "none",
  ...motion,
  ...focusRing,
  "&:hover": {
    backgroundColor: tokens.color.surface.hover,
    color: tokens.color.text.primary,
  },
  "&:disabled": {
    color: tokens.color.text.disabled,
  },
});

const StyledDeleteButton = styled(IconButton)({
  color: tokens.color.text.secondary,
  borderRadius: tokens.radius.sm,
  ...motion,
  ...focusRing,
  "&:hover": {
    color: tokens.color.status.error,
    backgroundColor: tokens.color.surface.hover,
  },
  "&:disabled": {
    color: tokens.color.text.disabled,
  },
});

interface DeleteIconButtonProps {
  onClick: (e: React.MouseEvent) => void;
  tooltipTitle?: string;
  size?: "small" | "medium" | "large";
  disabled?: boolean;
  filled?: boolean;
}

export const DeleteIconButton: React.FC<DeleteIconButtonProps> = ({
  onClick,
  tooltipTitle = "Delete",
  size = "medium",
  disabled = false,
  filled = false,
}) => {
  const Icon = filled ? DeleteIcon : DeleteOutlineIcon;

  return (
    <Tooltip title={tooltipTitle} arrow placement="top">
      <span>
        <StyledDeleteButton
          onClick={onClick}
          size={size}
          disabled={disabled}
          aria-label={tooltipTitle}
        >
          <Icon fontSize={size} />
        </StyledDeleteButton>
      </span>
    </Tooltip>
  );
};

interface StartIconButtonProps {
  onClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
  disabled?: boolean;
  icon?: React.ReactNode;
  style?: React.CSSProperties;
}

export const StartIconButton: React.FC<StartIconButtonProps> = ({
  onClick,
  children,
  disabled = false,
  icon = <AddIcon />,
  style = {},
}) => {
  return (
    <StyledTabButton
      startIcon={icon}
      onClick={onClick}
      disabled={disabled}
      style={style}
      sx={{
        "& .MuiButton-startIcon": {
          display: "inherit",
          marginRight: tokens.space[1],
          marginLeft: "-2px",
        },
      }}
    >
      {children}
    </StyledTabButton>
  );
};
